import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';

import { extractErrorMessage } from '../../api/errors';
import { getPostSurveyConfig, newStationCode, stationUrl } from '../../api/intake';

/**
 * The simulation station (POST-SURVEYS): a large QR code that participants
 * scan after a simulation to answer the sim post survey on their own phone.
 * Shown full screen on the station laptop, like the booth kiosk; Print gives
 * a poster to hang up instead. The code is made the first time the station
 * is opened and stays the same until someone chooses New code (which retires
 * any printed poster).
 */
export function PostSurveyStation({
  open,
  tenantId,
  onClose,
}: {
  open: boolean;
  tenantId: string;
  onClose: () => void;
}) {
  const [code, setCode] = useState<string | null>(null);
  const [surveyName, setSurveyName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setError(null);
    (async () => {
      try {
        let cfg = await getPostSurveyConfig(tenantId);
        if (!cfg.station_code) cfg = await newStationCode(tenantId);
        if (!alive) return;
        setCode(cfg.station_code);
        setSurveyName(cfg.sim_form?.name ?? null);
      } catch (e) {
        if (alive) setError(extractErrorMessage(e, 'could not open the station'));
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, tenantId]);

  const replace = async () => {
    setBusy(true);
    setError(null);
    try {
      setCode((await newStationCode(tenantId)).station_code);
    } catch (e) {
      setError(extractErrorMessage(e, 'could not make a new code'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen aria-labelledby="post-survey-station-title">
      <Toolbar sx={{ borderBottom: 1, borderColor: 'divider', gap: 2, displayPrint: 'none' }}>
        <Typography id="post-survey-station-title" variant="h6" component="h1" sx={{ flex: 1 }}>
          Post survey{surveyName ? ` · ${surveyName}` : ''}
        </Typography>
        <Button onClick={() => window.print()} disabled={!code}>
          Print
        </Button>
        <Button onClick={() => void replace()} disabled={busy || !code}>
          New code
        </Button>
        <IconButton edge="end" aria-label="Close" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Toolbar>
      <Stack
        spacing={3}
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        sx={{ flex: 1, p: { xs: 2, md: 4 } }}
      >
        <Typography variant="h3" component="h2">
          Finished your simulation?
        </Typography>
        <Typography variant="h6" color="text.secondary">
          Scan with your phone’s camera and answer a few questions about it.
        </Typography>
        <Box
          sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: 3, lineHeight: 0 }}
          aria-label="Post survey QR code"
          role="img"
        >
          {code ? (
            <QRCodeSVG value={stationUrl(code)} size={340} level="M" data-testid="station-qr" />
          ) : (
            <Box sx={{ width: 340, height: 340 }} />
          )}
        </Box>
        {code && (
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            {stationUrl(code)}
          </Typography>
        )}
        {error && <Alert severity="warning">{error}</Alert>}
      </Stack>
    </Dialog>
  );
}
